import { X } from 'lucide-react';
import type { Asociado } from '@lib/types';

/**
 * Modal informativo del resultado de validación contra Renaper para un
 * asociado. Muestra:
 *  - Estado actual (validado / no_coincide / no_encontrado / error / pendiente)
 *  - Motivo si hay error o no_encontrado
 *  - Tabla de campos pisados (cuando estado=no_coincide): valor anterior del
 *    padrón vs valor oficial Renaper. La pisada ya se aplicó al guardar la
 *    validación — esto es solo el audit trail visible para admin Medicus.
 *
 * Visible solo desde la vista admin de Medicus en `AsociadosTable`.
 */
interface Props {
  asociado: Asociado;
  onClose: () => void;
}

const CAMPO_LABEL: Record<string, string> = {
  apellSolt: 'Apellido',
  nombres: 'Nombres',
  fechaNacimiento: 'Fecha de nacimiento',
};

const ESTADO_UI: Record<string, { label: string; bg: string; color: string }> = {
  pendiente: { label: 'Pendiente de validar', bg: '#F1F5F9', color: '#475569' },
  validado: { label: 'Validado por Renaper', bg: '#DCFCE7', color: '#166534' },
  no_coincide: { label: 'Datos no coinciden', bg: '#FEF3C7', color: '#92400E' },
  no_encontrado: { label: 'No encontrado en Renaper', bg: '#FEE2E2', color: '#991B1B' },
  error: { label: 'Error de servicio', bg: '#FEE2E2', color: '#991B1B' },
};

export function RenaperDetalleModal({ asociado, onClose }: Props) {
  const rv = asociado.renaperValidacion;
  const estado = rv?.estado ?? 'pendiente';
  const ui = ESTADO_UI[estado] ?? ESTADO_UI.pendiente;
  const pisos = rv?.pisos ?? [];
  const consultadoFmt = rv?.consultadoAt
    ? new Date(rv.consultadoAt).toLocaleString('es-AR')
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="renaper-detalle-titulo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 12,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          maxWidth: 720,
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.35)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-outline)',
          }}
        >
          <div>
            <h2
              id="renaper-detalle-titulo"
              className="heading-5"
              style={{ margin: 0, color: 'var(--color-primary-darker)' }}
            >
              Detalle Renaper
            </h2>
            <p
              className="text-element-mid"
              style={{ fontSize: 12, margin: '2px 0 0' }}
            >
              {asociado.nombres} {asociado.apellSolt} · DNI {asociado.documento}
              {consultadoFmt ? ` · consultado ${consultadoFmt}` : ''}
            </p>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-element-mid)',
              padding: 4,
              display: 'flex',
            }}
          >
            <X size={20} />
          </button>
        </header>

        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          <div
            role="status"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '6px 12px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              color: ui.color,
              background: ui.bg,
              marginBottom: 16,
            }}
          >
            {ui.label}
          </div>

          {estado === 'validado' && (
            <p style={{ fontSize: 13, color: 'var(--color-element-mid)', margin: 0 }}>
              Apellido, nombres y fecha de nacimiento del padrón coinciden con los
              datos oficiales de Renaper.
            </p>
          )}

          {(estado === 'no_encontrado' || estado === 'error') && rv?.motivo && (
            <div
              style={{
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#991B1B',
                marginBottom: 8,
              }}
            >
              <strong style={{ display: 'block', marginBottom: 4 }}>
                {estado === 'no_encontrado' ? 'No se encontró en Renaper' : 'Error del servicio'}
              </strong>
              {rv.motivo}
              {estado === 'error' && (
                <p style={{ margin: '6px 0 0', fontSize: 12, fontStyle: 'italic' }}>
                  Reintentá la validación desde el botón de Renaper en la cabecera del padrón.
                </p>
              )}
            </div>
          )}

          {estado === 'no_coincide' && pisos.length > 0 && (
            <>
              <p style={{ fontSize: 13, color: 'var(--color-element-mid)', margin: '0 0 10px' }}>
                Los siguientes campos del padrón se actualizaron automáticamente
                con los valores oficiales de Renaper:
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                    border: '1px solid var(--color-outline)',
                  }}
                >
                  <thead>
                    <tr style={{ background: '#F8FAFC' }}>
                      <th style={th}>Campo</th>
                      <th style={th}>Valor original (padrón)</th>
                      <th style={th}>Valor pisado (Renaper)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pisos.map((p) => (
                      <tr key={p.campo}>
                        <td style={td}>{CAMPO_LABEL[p.campo] ?? p.campo}</td>
                        <td style={{ ...td, color: '#991B1B', textDecoration: 'line-through' }}>
                          {p.valorAnterior || '(vacío)'}
                        </td>
                        <td style={{ ...td, color: '#166534', fontWeight: 600 }}>
                          {p.valorNuevo || '(vacío)'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {estado === 'no_coincide' && pisos.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--color-element-mid)', margin: 0 }}>
              {rv?.motivo ??
                'Los datos no coinciden con Renaper pero no se pudo aplicar la corrección automática.'}
            </p>
          )}

          {estado === 'pendiente' && (
            <p style={{ fontSize: 13, color: 'var(--color-element-mid)', margin: 0 }}>
              Este asociado aún no fue validado contra Renaper. Dispará la validación
              desde el botón de Renaper en la cabecera del padrón.
            </p>
          )}
        </div>

        <footer
          style={{
            borderTop: '1px solid var(--color-outline)',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontWeight: 700,
  borderBottom: '1px solid var(--color-outline)',
};
const td: React.CSSProperties = {
  padding: '8px 10px',
  borderTop: '1px solid var(--color-outline)',
};
